import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom';
import ProfilInformations from '../../../components/Dashboard/accountManage/ProfilInformations';
import axiosClient from '../../../components/api/axiosClient';
import LoadContentSpinner from '../../../components/Utils/LoadContentSpinner';
import PersonnalDocumentID from '../../../components/Dashboard/accountManage/PersonnalDocumentID';
import PartnersContent from '../../../components/Dashboard/accountManage/PartnersContent';
import { useError } from '../../../components/contexts/ErrorContext';
import { useStateContext } from '../../../components/contexts/ContextProvider';

/**
 * POST /api/request/user/automations/weekly-sales-report-email
 * — Bearer (Sanctum) via axiosClient ; Content-Type application/json ; booléens JSON natifs.
 */
const WEEKLY_SALES_REPORT_EMAIL_ENDPOINT = '/request/user/automations/weekly-sales-report-email';

/** Lit la préférence depuis user.organizer_config (contexte /user) ou champs racine (API compte). */
function parseBoolLoose(v) {
    if (v === true || v === 1) return true;
    if (v === false || v === 0) return false;
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (s === 'true' || s === '1') return true;
        if (s === 'false' || s === '0' || s === '') return false;
    }
    return Boolean(v);
}

function resolveWeeklySalesReportEmail(accountLike) {
    if (!accountLike || typeof accountLike !== 'object') return false;
    const oc = accountLike.organizer_config;
    if (oc && typeof oc === 'object' && oc.weekly_sales_report_email != null) {
        return parseBoolLoose(oc.weekly_sales_report_email);
    }
    if (accountLike.weekly_sales_report_email != null) {
        return parseBoolLoose(accountLike.weekly_sales_report_email);
    }
    if (accountLike.notify_weekly_sales_report != null) {
        return parseBoolLoose(accountLike.notify_weekly_sales_report);
    }
    if (accountLike.rapport_ventes_lundi != null) {
        return parseBoolLoose(accountLike.rapport_ventes_lundi);
    }
    return false;
}

const ACCOUNT_PAGE_TITLES = {
    profil_informations: 'Mes Informations',
    notifications: 'Notifications',
    partnership: 'Partenariat',
    doc: 'Pièces jointes',
};

function resolveAccountTab(pathname, search) {
    if (pathname.includes('/notifications')) return 'notifications';
    if (pathname.includes('/partenariat')) return 'partnership';
    if (pathname.includes('/pieces-jointes')) return 'doc';
    const queryTab = new URLSearchParams(search).get('activeTab');
    if (queryTab === 'notifications' || queryTab === 'partnership' || queryTab === 'doc' || queryTab === 'profil_informations') {
        return queryTab;
    }
    return 'profil_informations';
}

export default function IndexAccountSetting() {

    const { user, setUser } = useStateContext();
    const location = useLocation();
    const activeTab = resolveAccountTab(location.pathname, location.search);

     // État pour gérer l'onglet actif
    // Initialiser les états pour chaque case à cocher sur "désactivé" (true)
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [twoFactorAuth, setTwoFactorAuth] = useState(true);
    const [invoiceAvailability, setInvoiceAvailability] = useState(true);
    const [supportNotifications, setSupportNotifications] = useState(true);
    /** Rapport des ventes par e-mail chaque lundi — synchronisé avec l’API */
    const [weeklySalesReportEmail, setWeeklySalesReportEmail] = useState(false);
    const [savingWeeklySalesReport, setSavingWeeklySalesReport] = useState(false);
    const [profilData, setProfilData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { handleError } = useError();

    // Case « rapport lundi » : priorité à user.organizer_config (contexte /user), sinon données GET compte
    useEffect(() => {
        const fromContext =
            user?.organizer_config && user.organizer_config.weekly_sales_report_email != null;
        if (fromContext) {
            setWeeklySalesReportEmail(resolveWeeklySalesReportEmail(user));
            return;
        }
        if (profilData) {
            setWeeklySalesReportEmail(resolveWeeklySalesReportEmail(profilData));
        }
    }, [user, profilData]);

    // recuperation des informations profil
        // Fonction de récupération des données
    const fetchProfilData = async () => {
        setLoading(true);
        setError(null);

        try {
            const { data } = await axiosClient.get('/request/user/getInformations/accountOrganizer');
            const accountUser = data.user || {};
            // L’API peut omettre organizer_config.weekly_sales_report_email : fusionner avec l’état précédent
            setProfilData((prev) => {
                if (!prev || typeof prev !== 'object') return accountUser;
                return {
                    ...accountUser,
                    organizer_config: {
                        ...(prev.organizer_config || {}),
                        ...(accountUser.organizer_config || {}),
                    },
                };
            });
        } catch (error) {
            handleError(error.message || 'Erreur survenue, veuillez réesayer !')
            // console.error("Erreur Axios capturée :", error.message);
            // setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Charger le profil pour « Mes informations » et « Notifications » (préférences)
    useEffect(() => {
        if (activeTab === 'profil_informations' || activeTab === 'notifications') {
            fetchProfilData();
        } else {
            setLoading(false);
        }
    }, [activeTab]);

    const handleWeeklySalesReportToggle = async () => {
        const next = !weeklySalesReportEmail;
        setSavingWeeklySalesReport(true);
        try {
            await axiosClient.post(
                WEEKLY_SALES_REPORT_EMAIL_ENDPOINT,
                { weekly_sales_report_email: Boolean(next) }
            );
            setWeeklySalesReportEmail(next);
            setProfilData((prev) =>
                prev
                    ? {
                          ...prev,
                          organizer_config: {
                              ...(prev.organizer_config || {}),
                              weekly_sales_report_email: next,
                          },
                          weekly_sales_report_email: next,
                      }
                    : prev
            );
            setUser((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    organizer_config: {
                        ...(prev.organizer_config || {}),
                        weekly_sales_report_email: next,
                    },
                };
            });
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                'Impossible d’enregistrer votre préférence. Réessayez.';
            handleError(msg);
        } finally {
            setSavingWeeklySalesReport(false);
        }
    };

    if (loading) {
        return <LoadContentSpinner />;
    }

    return (
        <div className="ch3yp cnbwt cs7xl clu2m c6btv clbq0 cxsfz">
            {/* Page header */}
            {/* <div className="cgd7w">
                
                <h1 className="text-gray-800 dark:text-gray-100 font-bold c459m cbtcb">
                {ACCOUNT_PAGE_TITLES[activeTab]}
                </h1>
                
            </div> */}

            <div className="bg-white c2vpa c1hly c5vqk cgd7w">
                <div className="cbw8w">
                        {/* Panel body */}
                        {activeTab === 'profil_informations' && (
                            <ProfilInformations profilData={profilData} refreshData={fetchProfilData} />
                        )}
                        {activeTab === 'notifications' && (
                            <div>
                                <div className="cmxzb c41yb">
                                    <h2 className="text-gray-800 dark:text-gray-100 font-bold cbtcb c6r0l">
                                        {ACCOUNT_PAGE_TITLES[activeTab]}
                                    </h2>
                                    <section>
                                        <ul>
                                            <li className="flex items-center border-gray-200 cghq3 cm3rx ctv3r c72q5">
                                                {/* Left */}
                                                <div>
                                                <div className="text-gray-800 dark:text-gray-100 cgulq">
                                                Notifications par e-mail
                                                </div>
                                                <div className="text-sm">
                                                Recevez des mises à jour importantes et des alertes directement dans votre boîte de réception pour rester informé de toutes les actualités et activités liées à votre compte.
                                                </div>
                                                </div>
                                                {/* Right */}
                                                <div className="flex items-center cfh3y">
                                                <div
                                                    className="text-sm mr-2 cmpw7 cdqku caf78"
                                                >
                                                    Oui
                                                </div>
                                                <div className="c1ivc">
                                                    <input
                                                    type="checkbox"
                                                    checked={emailNotifications}
                                                    onChange={() => setEmailNotifications(true)} // Toujours désactivé
                                                    className="cn8jz"
                                                    disabled
                                                    />
                                                    <label className="dark:bg-gray-700 cg3qz">
                                                    <span className="bg-white c5vqk" aria-hidden="true" />
                                                    {/* <span className="cn8jz">Enable smart sync</span> */}
                                                    </label>
                                                </div>
                                                </div>
                                            </li>
                                            <li className="flex items-center border-gray-200 cghq3 cm3rx ctv3r c72q5">
                                                {/* Left */}
                                                <div>
                                                <div className="text-gray-800 dark:text-gray-100 cgulq">Authentification à deux facteurs (2FA)</div>
                                                <div className="text-sm">
                                                Une couche de sécurité supplémentaire pour protéger votre compte en exigeant une vérification supplémentaire lors de la connexion ou tout autres actions d'authenticité.
                                                </div>
                                                </div>
                                                {/* Right */}
                                                <div className="flex items-center cfh3y">
                                                <div
                                                    className="text-sm mr-2 cmpw7 cdqku caf78"
                                                >
                                                    Oui
                                                </div>
                                                <div className="c1ivc">
                                                    <input
                                                    type="checkbox"
                                                    checked={twoFactorAuth}
                                                    onChange={() => setTwoFactorAuth(true)}
                                                    className="cn8jz"
                                                    disabled
                                                    />
                                                    <label className="dark:bg-gray-700 cg3qz">
                                                    <span className="bg-white c5vqk" aria-hidden="true" />
                                                    {/* <span className="cn8jz">Enable smart sync</span> */}
                                                    </label>
                                                </div>
                                                </div>
                                            </li>
                                            <li className="flex items-center border-gray-200 cghq3 cm3rx ctv3r c72q5">
                                                {/* Left */}
                                                <div>
                                                <div className="text-gray-800 dark:text-gray-100 cgulq">Disponibilite des factures</div>
                                                <div className="text-sm">
                                                Soyez informé dès qu'une nouvelle facture est disponible pour consultation ou téléchargement sur votre compte.
                                                </div>
                                                </div>
                                                {/* Right */}
                                                <div className="flex items-center cfh3y">
                                                <div
                                                    className="text-sm mr-2 cmpw7 cdqku caf78"
                                                >
                                                    Oui
                                                </div>
                                                <div className="c1ivc">
                                                    <input
                                                    type="checkbox"
                                                    checked={invoiceAvailability}
                                                    onChange={() => setInvoiceAvailability(true)}
                                                    className="cn8jz"
                                                    disabled
                                                    />
                                                    <label className="dark:bg-gray-700 cg3qz">
                                                    <span className="bg-white c5vqk" aria-hidden="true" />
                                                    {/* <span className="cn8jz">Enable smart sync</span> */}
                                                    </label>
                                                </div>
                                                </div>
                                            </li>
                                            <li className="flex items-center cghq3 cm3rx c72q5">
                                                {/* Left */}
                                                <div>
                                                <div className="text-gray-800 dark:text-gray-100 cgulq">Notification du support</div>
                                                <div className="text-sm">
                                                Recevez des notifications de l'équipe de support pour des mises à jour sur les tickets ou les demandes d'assistance en cours.
                                                </div>
                                                </div>
                                                {/* Right */}
                                                <div className="flex items-center cfh3y">
                                                <div
                                                    className="text-sm mr-2 cmpw7 cdqku caf78"
                                                >
                                                    Oui
                                                </div>
                                                <div className="c1ivc">
                                                    <input
                                                    type="checkbox"
                                                    checked={supportNotifications}
                                                    onChange={() => setSupportNotifications(true)}
                                                    className="cn8jz"
                                                    disabled
                                                    />
                                                    <label className="dark:bg-gray-700 cg3qz">
                                                    <span className="bg-white c5vqk" aria-hidden="true" />
                                                    {/* <span className="cn8jz">Enable smart sync</span> */}
                                                    </label>
                                                </div>
                                                </div>
                                            </li>
                                            <li className="flex flex-col sm:flex-row sm:items-center gap-3 border-t border-gray-200 dark:border-gray-700 border-b-0 cghq3 cm3rx ctv3r c72q5 pt-5 mt-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-gray-800 dark:text-gray-100 cgulq">
                                                        Rapport de billetterie (envoie tous les lundis)
                                                    </div>
                                                    <div className="text-sm ">
                                                        Recevez par e-mail un récapitulatif de votre activité de ventes. Ce réglage est enregistré immédiatement sur votre compte.
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 cfh3y">
                                                    <span
                                                        className={`text-sm cmpw7 cdqku ${weeklySalesReportEmail ? 'text-primary-600 dark:text-primary-400 font-medium' : 'caf78'}`}
                                                        aria-live="polite"
                                                    >
                                                        {savingWeeklySalesReport
                                                            ? 'Enregistrement…'
                                                            : weeklySalesReportEmail
                                                              ? 'Oui'
                                                              : 'Non'}
                                                    </span>
                                                    <div className={`c1ivc ${savingWeeklySalesReport ? 'opacity-60 pointer-events-none' : ''}`}>
                                                        <input
                                                            id="weekly-sales-report-email"
                                                            type="checkbox"
                                                            checked={weeklySalesReportEmail}
                                                            onChange={handleWeeklySalesReportToggle}
                                                            className="cn8jz"
                                                            disabled={savingWeeklySalesReport}
                                                            aria-label="Recevoir le rapport des ventes chaque lundi par e-mail"
                                                        />
                                                        <label
                                                            htmlFor="weekly-sales-report-email"
                                                            className="dark:bg-gray-700 cg3qz cursor-pointer"
                                                        >
                                                            <span className="bg-white c5vqk" aria-hidden="true" />
                                                        </label>
                                                    </div>
                                                </div>
                                            </li>
                                        </ul>
                                    </section>
                                </div>
                            </div>
                        )}
                        {activeTab === 'partnership' && (
                            <PartnersContent />
                        )}
                        {activeTab === 'doc' && (
                            <PersonnalDocumentID />
                        )}
                </div>
            </div>

        </div>
    )
}
